"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Formatter = void 0;
const ibantools_1 = require("ibantools");
const luxon_1 = require("luxon");
const Sorter_1 = require("./Sorter");
function pad(number) {
    if (number < 10) {
        return '0' + number;
    }
    return number;
}
class Formatter {
    static removeAccents(name) {
        name = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        return name;
    }
    static emailSenderName(name) {
        return this.removeAccents(name).replace(/[^A-Za-z]+/g, " ").trim();
    }
    static iban(ibanRaw) {
        var _a;
        try {
            return (_a = (0, ibantools_1.friendlyFormatIBAN)(ibanRaw)) !== null && _a !== void 0 ? _a : ibanRaw; // 'NL91ABNA0517164300'
        }
        catch (e) {
            console.error("Invalid IBAN", ibanRaw, e);
            return ibanRaw; // Return the raw IBAN if it cannot be formatted
        }
    }
    static slug(name) {
        return this.removeAccents(name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+/, "").replace(/-+$/, "");
    }
    static slugEmail(name) {
        return this.removeAccents(name).toLowerCase().replace(/[^a-z0-9_]+/g, "-").replace(/^-+/, "").replace(/-+$/, "");
    }
    static fileSlug(name) {
        return this.removeAccents(name).replace(/[^A-Za-z0-9-]+/g, " ").trim();
    }
    static sepaPaymentSlug(name) {
        // https://www.europeanpaymentscouncil.eu/document-library/guidance-documents/sepa-requirements-extended-character-set-unicode-subset-best
        return this.removeAccents(name).replace(/[^A-Za-z0-9-.:'+]+/g, " ").trim();
    }
    static removeDuplicateSpaces(name) {
        return name.replace(/\s+/, " ");
    }
    /**
     * 1 = january
     */
    static month(index) {
        if (typeof index === "object") {
            const datetime = luxon_1.DateTime.fromJSDate(index).setZone(this.timezone);
            index = datetime.month;
        }
        const monthNames = ["januari", "februari", "maart", "april", "mei", "juni",
            "juli", "augustus", "september", "oktober", "november", "december"
        ];
        return monthNames[index - 1];
    }
    static weekDay(date) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        const monthNames = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag",];
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
        return monthNames[datetime.weekday - 1];
    }
    /**
     * 1 januari (2020). Year only in different year if withYear is null (default)
     */
    static date(date, withYear = null) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        const currentYear = luxon_1.DateTime.now().setZone(this.timezone).year;
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
        const year = datetime.year;
        return datetime.day + " " + this.month(datetime.month) + (currentYear != year || withYear === true ? (" " + year) : "");
    }
    /**
     * januari 2020
     */
    static dateWithoutDay(date, options) {
        var _a;
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone((_a = options === null || options === void 0 ? void 0 : options.timezone) !== null && _a !== void 0 ? _a : this.timezone);
        const year = datetime.year;
        return this.month(datetime.month) + " " + year;
    }
    /**
     * Year in Brussels timezone - not UTC
     * 2020
     */
    static year(date) {
        if (!date) {
            // Crash protection in case undefined get passed
            return 0;
        }
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
        return datetime.year;
    }
    /**
     * Day in Brussels timezone - not UTC
     * 31
     */
    static day(date) {
        if (!date) {
            // Crash protection in case undefined get passed
            return 0;
        }
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
        return datetime.day;
    }
    /**
     * maandag, 1 januari (2020). Year only in different year
     */
    static dateWithDay(date) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        return this.weekDay(date) + ", " + this.date(date);
    }
    /**
     * maandag, 1 januari (2020) om XX:XX. Year only in different year
     */
    static dateTimeWithDay(date) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        return this.weekDay(date) + ", " + this.dateTime(date);
    }
    /**
    * 01/01/2020
    */
    static dateNumber(date, withYear = true) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
        return (datetime.day + "").padStart(2, "0") + "/" + (datetime.month + "").padStart(2, "0") + (withYear ? "/" + datetime.year : "");
    }
    /**
     * 2020-01-31
     */
    static dateIso(date) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
        const year = datetime.year;
        return year + "-" + (datetime.month + "").padStart(2, "0") + "-" + (datetime.day + "").padStart(2, "0");
    }
    /**
    * 2020-01-31 00:00:00
    */
    static dateTimeIso(date, timezone) {
        if (!date) {
            // Crash protection in case undefined get passed
            return '?';
        }
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(timezone !== null && timezone !== void 0 ? timezone : this.timezone);
        const year = datetime.year;
        return year + '-' + (datetime.month + '').padStart(2, '0') + '-' + (datetime.day + '').padStart(2, '0') + ' ' + (datetime.hour + '').padStart(2, '0') + ':' + (datetime.minute + '').padStart(2, '0') + ':' + (datetime.second + '').padStart(2, '0');
    }
    /**
     * 12:00
     */
    static time(date) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
        return datetime.hour + ":" + (datetime.minute + "").padStart(2, "0");
    }
    static luxon(date = new Date()) {
        return luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
    }
    /**
     * 12:00
     */
    static timeIso(date) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        const datetime = luxon_1.DateTime.fromJSDate(date).setZone(this.timezone);
        return (datetime.hour + "").padStart(2, "0") + ":" + (datetime.minute + "").padStart(2, "0");
    }
    static uniqueArray(array) {
        function onlyUnique(value, index, self) {
            return self.indexOf(value) === index;
        }
        return array.filter(onlyUnique);
    }
    /**
     * 1 januari (2020) om 12:00. Year only in different year
     */
    static dateTime(date, hideZero = false) {
        if (!date) {
            // Crash protection in case undefined get passed
            return "?";
        }
        if (hideZero && this.time(date) == "0:00") {
            return this.date(date);
        }
        return this.date(date) + " om " + this.time(date);
    }
    static integer(value) {
        const formatted = new Intl.NumberFormat("nl-BE").format(Math.abs(value));
        const v = (value < 0 ? "- " : "") + formatted;
        return v;
    }
    static percentage(value) {
        const formatted = new Intl.NumberFormat("nl-BE").format(Math.abs(value / 100));
        const v = (value < 0 ? "- " : "") + formatted;
        return v + "%";
    }
    static price(value, removeZeroDecimals = true) {
        const formatted = new Intl.NumberFormat("nl-BE", {
            style: "currency",
            currency: "EUR",
        }).format(Math.abs(value) / 100);
        const v = (value < 0 ? "- " : "") + formatted.replace(new RegExp("EUR", "ig"), '€');
        if (removeZeroDecimals && (v.endsWith(",00") || v.endsWith(".00"))) {
            return v.substring(0, v.length - 3);
        }
        return v;
    }
    static priceChange(value) {
        if (value >= 0) {
            return "+ " + this.price(value);
        }
        return this.price(value);
    }
    static capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }
    static capitalizeWords(string) {
        return string.split(" ").map(s => this.capitalizeFirstLetter(s)).join(" ");
    }
    static fileSize(bytes) {
        if (bytes < 1000) {
            return bytes + " bytes";
        }
        if (bytes < 1000 * 1000) {
            return Math.round(bytes / 1000) + " kB";
        }
        if (bytes < 1000 * 1000 * 1000) {
            return Math.round(bytes / 1000 / 100) / 10 + " MB";
        }
        return Math.round(bytes / 1000 / 1000 / 10) / 100 + " GB";
    }
    static minutesPadded(minutes) {
        const h = Math.floor(minutes / 60);
        const m = minutes - h * 60;
        return (h + "").padStart(2, "0") + ":" + (m + "").padStart(2, "0");
    }
    static minutes(minutes) {
        const h = Math.floor(minutes / 60);
        const m = minutes - h * 60;
        return h + ":" + (m + "").padStart(2, "0");
    }
    static escapeHtml(unsafe) {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }
    static joinLast(array, separator, lastSeparator) {
        array = array.slice();
        const last = array.pop();
        if (last === undefined) {
            return "";
        }
        if (array.length == 0) {
            return last + "";
        }
        return array.join(separator) + lastSeparator + last;
    }
    static ordinalNumber(number) {
        if (number == 1) {
            return "eerste";
        }
        if (number == 2) {
            return "tweede";
        }
        if (number == 3) {
            return "derde";
        }
        return number + "e";
    }
    static pluralText(num, singular, plural) {
        if (num === 1) {
            return 'één ' + singular;
        }
        return num + ' ' + plural;
    }
    static groupNamesByFamily(names) {
        const n = names.slice().sort((a, b) => Sorter_1.Sorter.stack(a.lastName.localeCompare(b.lastName), a.firstName.localeCompare(b.firstName)));
        const firstNames = this.uniqueArray(n.map(n => n.firstName));
        const lastNames = this.uniqueArray(n.map(n => n.lastName));
        return this.joinLast(firstNames, ", ", " en ") + (lastNames.length > 0 ? (" " + lastNames.join('-')) : '');
    }
    static firstLetters(str, maxLength) {
        if (maxLength === 1) {
            return str.substr(0, 1).toLocaleUpperCase();
        }
        const splitted = Formatter.slug(str).split('-');
        return splitted.slice(0, maxLength).map(s => s.substr(0, 1).toLocaleUpperCase()).join('');
    }
}
exports.Formatter = Formatter;
Formatter.timezone = "Europe/Brussels";
//# sourceMappingURL=Formatter.js.map